import { useQuery } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";
import { getStatusMeta } from "../../constants/order-status";

// useTatFilters — options for the TAT filter row (status chips, sort-by chips).
//
// GET /api/ops/tat/filters returns the LIVE status list read straight from
// Postgres `pg_enum` (pillup-api/controller/ops/tat.js) — not from any JS
// model or constants file, both of which disagree with the database
// (reference-status-vocabularies.md). Each code arrives with a generic
// mechanically-humanized label (`SHIPPED_IN_TRANSIT` -> "Shipped In
// Transit") — deliberately dumb server-side, because the *nice* label
// mapping (several raw codes sharing one human label, e.g. RX_SENT and
// ORDER_PENDING both reading "Order Pending") already lives in this
// frontend's own consolidated registry (src/v2/constants/order-status.js,
// built from the legacy orders.jsx filter groups). Re-deriving that mapping
// server-side would just be a second copy to keep in sync; instead this
// hook re-labels every live code through `getStatusMeta` and groups by
// THAT label, so an ops user sees the same names as the row-level status
// pills, while the codes list still comes from the server (a code the server
// doesn't know about can't be filtered on).
//
// `statusOptions` is `{ label, codes: string[] }[]` — kept for any future
// per-code filter UI, but the CHIP ROW on the page now uses `statusGroups`
// below instead of this, for the reason in that field's own comment.
//
// `statusGroups` and `sortOptions` are passed through VERBATIM from the
// server, not re-derived. Phase 13 (pillup-api/docs/tat-dashboard/
// execution-plan.md) added both specifically so "the frontend stops
// hardcoding chip definitions" — this hook previously (before Phase 13
// shipped) had its own hardcoded `statusCodesForGroup` expansion in
// src/v2/ops/constants/tatFilters.js, which had already drifted from the real
// server-side groupings by the time Phase 13 landed (e.g. this frontend put
// ORDER_PENDING/ORDER_ACTIVE under "To Process"; the server's real QUOTATION
// group puts them there instead). Passing the server's own list through
// verbatim is what makes that class of bug structurally impossible going
// forward — the client can't drift from a list it doesn't own a copy of.
//
// Each `statusGroups` entry is `{ key, label, statuses: string[] }`, except
// the exclusion-based "In Progress" chip, which is `{ key, label, excludes:
// string[] }` — the caller never needs to branch on this: the client sends
// `statusGroup=<key>` on the wire either way and the server does the
// expansion (or exclusion) itself. See buildTatFilterParams in
// useTatOrders.js.
//
// `staleTime: 300000` (5 minutes) — filter options change on a deploy
// cadence, not a per-click one.

export default function useTatFilters() {
    return useQuery(
        ["tat-filters"],
        async () => {
            const result = await request({
                url: "/ops/tat/filters",
                method: "GET",
            });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;

            const { statuses = [], statusGroups = [], sortOptions = [] } = body.data;

            // Group codes by THIS frontend's label, preserving first-seen
            // order both across labels and within each label's code list.
            const codesByLabel = new Map();
            statuses.forEach(({ code }) => {
                const { label } = getStatusMeta(code);
                const codes = codesByLabel.get(label) ?? [];
                codes.push(code);
                codesByLabel.set(label, codes);
            });
            const statusOptions = Array.from(codesByLabel, ([label, codes]) => ({ label, codes })).sort(
                (a, b) => a.label.localeCompare(b.label)
            );

            return { statusOptions, statusGroups, sortOptions };
        },
        {
            staleTime: 300000,
            retry: false,
        }
    );
}
